import prepareURL from 'src/common/prepareURL';
import { getSessionStorage } from 'src/common/storage';
import { injectTags } from 'src/pages/analytics/tag/api/tagApiSlice';
import apiSlice from 'src/reducers/services/api';

import { capitalizeFirstLetter } from '../../components/utils/Capitilizer';
import { Store, StoreParams, StoresApiResponse } from '../../types/stores/StoresApiResponse';

const storesBasePath = 'business/stores';
const filePath = '/../files/';

const storeApiSlice = apiSlice.injectEndpoints({
	endpoints: (builder) => ({
		stores: builder.query<StoresApiResponse, StoreParams>({
			query: (args) => {
				return prepareURL(`business/${args?.query?.business_id}/store`, args);
			},
			transformResponse: async (response: any, meta: any, arg: any) => {
				let withTags = await injectTags({ items: response?.result, api: response._api, extraOptions: response._extraOptions });

				return withTags.map((store: Store) => ({
					_id: store['_id'],
					title: store?.title,
					location: `${store?.location['street_address']}, ${store?.location['city']}, ${store?.location['post_code']}, ${store?.location['country']}`,
					description: store?.description,
					number: store?.phone?.val,
					published: store?.published,
					phone: store?.phone,
					tags: store?.tags,
				}));
			},
			providesTags: [{ type: 'Stores' }],
		}),
		getStoreDetails: builder.query({
			query: (args) => {
				const bid = JSON.parse(getSessionStorage('act_bsn'))?._id;
				return {
					method: 'GET',
					url: prepareURL(`business/${bid ?? args?.bid}/store/${args?.path?.id}`, args),
				};
			},
			providesTags: ['StoreDetails'],
		}),
		getStoreTypes: builder.query({
			query: (args) => ({
				method: 'GET',
				url: prepareURL(`${storesBasePath}/types`, args),
			}),
			transformResponse: (response: any) => {
				return response['types'].map((category: { _id: string; title: string }) => ({
					value: category._id,
					label: capitalizeFirstLetter(category.title),
				}));
			},
		}),
		getStoreTraits: builder.query({
			query: (args) => ({
				method: 'GET',
				url: prepareURL(`${storesBasePath}/traits`, args),
			}),
			transformResponse: (response: any) => {
				const types = ['tags', 'payment_methods', 'suggested_for', 'environment', 'misc', 'services', 'info'];
				return response?.traits
					.filter((item: { type: string }) => types.includes(item.type))
					.map((trait: { _id: string; title: string; type: string }) => ({
						value: trait?._id,
						label: capitalizeFirstLetter(trait?.title),
						type: trait?.type,
					}));
			},
		}),
		getCuisine: builder.query({
			query: (args) => ({
				method: 'GET',
				url: prepareURL(`${storesBasePath}/cuisines`, args),
			}),
			transformResponse: (response: any) => {
				return response['cuisines'].map((cuisine: { _id: string; title: string }) => ({
					value: cuisine._id,
					label: capitalizeFirstLetter(cuisine.title),
				}));
			},
		}),
		getReservationPreferences: builder.query({
			query: (args) => ({
				method: 'GET',
				url: prepareURL(`${storesBasePath}/reservation-prefs`, args),
			}),
			transformResponse: (response: any) => {
				return response['rsv_prefs'].map((preference: { _id: string; title: string }) => ({
					value: preference._id,
					label: capitalizeFirstLetter(preference.title),
				}));
			},
			providesTags: ['ReservationPreferences'],
		}),
		createStore: builder.mutation({
			query: (args) => {
				const bid = JSON.parse(getSessionStorage('act_bsn'))?._id;
				return {
					method: 'POST',
					url: prepareURL(`business/${bid ?? args?.bid}/store`, args),
					body: args.body,
				};
			},
			invalidatesTags: [{ type: 'Stores' }, { type: 'BusinessByRole' }],
		}),
		editStore: builder.mutation({
			query: (args) => {
				const bid = JSON.parse(getSessionStorage('act_bsn'))?._id;
				return {
					method: 'PUT',
					url: prepareURL(`business/${bid ?? args?.bid}/store/${args?.path.id}`, args),
					body: args.body,
				};
			},
			async onQueryStarted(arg, { dispatch, queryFulfilled }) {
				try {
					const { data: updatedStore } = await queryFulfilled;
					// Update the store in the business slice
					dispatch({
						type: 'business/updateStoreInBusiness',
						payload: {
							storeId: arg.path.id,
							updates: {
								title: updatedStore.title,
							},
						},
					});
				} catch (err) {
					console.error('Failed to update store:', err);
				}
			},
			invalidatesTags: [{ type: 'Stores' }, { type: 'StoreDetails' }, { type: 'BusinessByRole' }],
		}),
		deleteStore: builder.mutation({
			query: (args) => {
				const bid = JSON.parse(getSessionStorage('act_bsn'))?._id;
				return {
					method: 'DELETE',
					url: prepareURL(`business/${bid ?? args?.bid}/store/${args?.path.id}`, args),
				};
			},
			async onQueryStarted(arg, { dispatch, queryFulfilled }) {
				try {
					await queryFulfilled;
					// Remove the store from the business slice
					dispatch({
						type: 'business/removeStoreFromBusiness',
						payload: {
							storeId: arg.path.id,
						},
					});
				} catch (err) {
					console.error('Failed to delete store:', err);
				}
			},
			invalidatesTags: [{ type: 'Stores' }, { type: 'BusinessByRole' }],
		}),
		uploadStoreFiles: builder.mutation({
			query: (args) => ({
				url: prepareURL(filePath + 'upload-file', args),
				method: 'POST',
				body: args,
			}),
		}),
		uploadStoreImage: builder.mutation({
			query: (args) => ({
				url: prepareURL(filePath + 'upload-image', args),
				method: 'POST',
				body: args,
			}),
		}),
		getTableTypes: builder.query({
			query: (args) => ({
				method: 'GET',
				url: prepareURL(`${storesBasePath}/table-types`, args),
			}),
			transformResponse: (response: any) => {
				return response?.table_types.map((type: { key: string; title: string }) => ({
					value: type.key,
					label: capitalizeFirstLetter(type.title),
				}));
			},
		}),
	}),
});

export const {
	useStoresQuery,
	useGetStoreDetailsQuery,
	useGetStoreTypesQuery,
	useGetStoreTraitsQuery,
	useGetCuisineQuery,
	useGetReservationPreferencesQuery,
	useCreateStoreMutation,
	useEditStoreMutation,
	useDeleteStoreMutation,
	useUploadStoreImageMutation,
	useUploadStoreFilesMutation,
	useGetTableTypesQuery,
} = storeApiSlice;
